import React, { useMemo } from 'react';
import c from 'classnames';
import Immutable from 'immutable';

import { Taxonomy } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { Quicklinks } from '../../../components';
import taxonomyQuicklinksMapper from '../../../components/Quicklinks/taxonomyQuicklinksMapper';
import { DEFAULT_LANGUAGE, VIEW_TAXONOMY_THEMES_MAX_COLUMNS_PER_ROW } from '../../../config/constants';
import type { ParagraphThemesHighlighted as Type } from '../custom';

export interface ParagraphThemesHighlightedProps {
  entity: ImmutableEntity<Type>;
  className?: string;
}

const ParagraphThemesHighlighted: React.FC<ParagraphThemesHighlightedProps> = (props) => {
  const { entity, className } = props;

  const classNames = c('ParagraphThemesHighlighted', className);
  const paragraph = entity.toJS();
  const uuid = paragraph.uuid[0].value;
  const title = paragraph.field_title[0]?.value;
  const themes = paragraph.field_themes;

  const colClassName = useMemo(() => (
    themes.length < VIEW_TAXONOMY_THEMES_MAX_COLUMNS_PER_ROW ? 'col-md-6' : 'col-md-6 col-xl-4'
  ), [themes.length]);

  return (
    <div id={uuid} className={classNames}>
      {title && (
        <h2>{title}</h2>
      )}

      <div className="d-md-none mt-6 mb-10">
        <Quicklinks links={taxonomyQuicklinksMapper(themes)} />
      </div>

      <Container>
        <div className="row u-gap--row--6 u-gap--row--md--8 mt-6 mt-md-8">
          {themes.map((theme) => {
            // We devide theme taxonomy terms into themes and subjects, where subjects are
            // the child taxonomy terms of themes. Therefore, when a term has a parent id,
            // we know it is a subject and thus we  have to use that subject's parent id to
            // render the entity.
            const parentId = theme.entity?.parent[0]?.target_id;
            const id = parentId || theme.target_id;
            const language = theme.entity?.langcode[0].value || DEFAULT_LANGUAGE;
            const slug = (theme.entity?.name[0].value ?? '').replace(/ /g, '-').toLowerCase();

            return (
              <div
                id={slug}
                key={`taxonomy:${id}:${language}`}
                className={colClassName}
              >
                <Taxonomy
                  viewMode="teaser"
                  {...(parentId ? {
                    id,
                  } : {
                    entity: Immutable.fromJS(theme.entity),
                  })}
                />
              </div>
            );
          })}
        </div>
      </Container>
    </div>
  );
};

export default ParagraphThemesHighlighted;
